/**
 * CustomReportTemplateEditor (KAN-87)
 * Full editor for creating/editing custom report templates.
 * Sections: Settings, CSV Options, Default Filters, Columns (embedded OutputFormatsTemplateEditor).
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { customReportService } from '../../services/api/customReportService';
import OutputFormatsTemplateEditor from '../outputFormats/OutputFormatsTemplateEditor';
import type { ColumnEditorRef } from '../outputFormats/OutputFormatsTemplateEditor';
import CustomReportFilterPanel from './CustomReportFilterPanel';
import AlertModal from '../common/AlertModal';
import type {
  AssetClass,
  CustomReportTemplate,
  TradeFilters,
  FilterOptions,
  TradesSourceSchema,
} from '../../types/customReports';
import { DEFAULT_TRADE_FILTERS } from '../../types/customReports';
import type {
  OutputFormat,
  CsvOptions,
  ColumnRule,
  EnumMapping,
  CalculatedColumn,
  ConditionalColumn,
  SortSpec,
  SourceSchema,
} from '../../types/outputFormats';
import './CustomReports.css';

interface CustomReportTemplateEditorProps {
  clientId: string;
  uid: string;
  templateId?: string;  // undefined = create mode
  scope?: 'USER' | 'SHARED';  // default USER
  /** Fixed source type for new templates. Ignored when editing (template's own sourceType is used). */
  initialSourceType?: 'TRADES' | 'EVENT_LEVEL_REPORTS';
  /** Asset class context — set by the page (FX or Commodities). Defaults to 'FX'. */
  assetClass?: AssetClass;
  onSave: () => void;
  onCancel: () => void;
}

const CustomReportTemplateEditor: React.FC<CustomReportTemplateEditorProps> = ({
  clientId,
  uid,
  templateId,
  scope = 'USER',
  initialSourceType,
  assetClass = 'FX',
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const isEditMode = !!templateId;
  const editorRef = useRef<ColumnEditorRef>(null);

  // Source type — set from initialSourceType when creating, or from loaded template when editing
  const [resolvedSourceType, setResolvedSourceType] = useState<'TRADES' | 'EVENT_LEVEL_REPORTS'>(
    initialSourceType || 'TRADES',
  );

  // Settings
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('CSV');
  const [csvOptions, setCsvOptions] = useState<CsvOptions>({
    delimiter: ',',
    encoding: 'UTF-8',
    includeHeader: true,
    lineEnding: 'CRLF',
  });

  // Filters
  const [tradeFilters, setTradeFilters] = useState<TradeFilters>(DEFAULT_TRADE_FILTERS);

  // Source schema
  const [sourceSchema, setSourceSchema] = useState<TradesSourceSchema | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);

  // Initial column state (passed to embedded editor)
  const [initialColumns, setInitialColumns] = useState<ColumnRule[]>([]);
  const [initialEnumMappings, setInitialEnumMappings] = useState<EnumMapping[]>([]);
  const [initialCalculatedColumns, setInitialCalculatedColumns] = useState<CalculatedColumn[]>([]);
  const [initialConditionalColumns, setInitialConditionalColumns] = useState<ConditionalColumn[]>([]);
  const [initialSortBy, setInitialSortBy] = useState<SortSpec[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [csvOptionsExpanded, setCsvOptionsExpanded] = useState(false);

  // Alert modal
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  const showError = (message: string) => {
    setAlertModal({ isOpen: true, type: 'error', title: t('common.error', 'Error'), message });
  };

  // ========== Load data ==========

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [schema, options] = await Promise.all([
          customReportService.getSourceSchema(clientId, resolvedSourceType, assetClass),
          customReportService.getFilterOptions(clientId, assetClass),
        ]);
        if (cancelled) return;
        setSourceSchema(schema);
        setFilterOptions(options);

        if (templateId) {
          const template = scope === 'SHARED'
            ? await customReportService.getSharedTemplate(clientId, templateId)
            : await customReportService.getUserTemplate(clientId, uid, templateId);
          if (cancelled) return;
          setName(template.name);
          setDescription(template.description || '');
          setOutputFormat(template.outputFormat);
          if (template.csvOptions) setCsvOptions(template.csvOptions);
          if (template.tradeFilters) setTradeFilters(template.tradeFilters);
          // Resolve source type from loaded template
          if (template.sourceType) setResolvedSourceType(template.sourceType);
          // Initial column state for embedded editor
          setInitialColumns(template.columns || []);
          setInitialEnumMappings(template.enumMappings || []);
          setInitialCalculatedColumns(template.calculatedColumns || []);
          setInitialConditionalColumns(template.conditionalColumns || []);
          setInitialSortBy(template.sortBy || []);
        } else if (initialSourceType === 'EVENT_LEVEL_REPORTS') {
          // New event-level template — pre-fill period to current month
          const now = new Date();
          const y = now.getFullYear();
          const m = now.getMonth() + 1;
          const monthStr = String(m).padStart(2, '0');
          const lastDay = new Date(y, m, 0).getDate();
          setTradeFilters(prev => ({
            ...prev,
            eventPeriodFrom: `${y}-${monthStr}-01`,
            eventPeriodTo: `${y}-${monthStr}-${String(lastDay).padStart(2, '0')}`,
          }));
        }
      } catch (err: any) {
        console.error('Error loading template editor data:', err);
        if (!cancelled) showError(err.message || t('customReports.messages.loadEditorError', 'Failed to load editor data'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [clientId, uid, templateId, assetClass, resolvedSourceType]);

  // ========== Save ==========

  const handleSave = async () => {
    // Validate
    if (!name.trim()) {
      showError(t('customReports.editor.nameRequired', 'Report name is required'));
      return;
    }

    // Get column state from embedded editor
    const columnState = editorRef.current?.getColumnState();
    if (!columnState || columnState.columns.length === 0) {
      showError(t('customReports.editor.columnsRequired', 'At least one column is required'));
      return;
    }

    setSaving(true);
    try {
      const template: Omit<CustomReportTemplate, 'templateId'> = {
        name: name.trim(),
        description: description.trim() || undefined,
        sourceType: resolvedSourceType,
        assetClass,
        outputFormat,
        scope,
        isDefault: false,
        isActive: true,
        csvOptions: outputFormat === 'CSV' ? csvOptions : undefined,
        columns: columnState.columns,
        enumMappings: columnState.enumMappings,
        calculatedColumns: columnState.calculatedColumns,
        conditionalColumns: columnState.conditionalColumns,
        concatenatedColumns: columnState.concatenatedColumns,
        sortBy: columnState.sortBy.length > 0 ? columnState.sortBy : undefined,
        tradeFilters,
      };

      if (templateId) {
        if (scope === 'SHARED') {
          await customReportService.updateSharedTemplate(clientId, templateId, template);
        } else {
          await customReportService.updateUserTemplate(clientId, uid, templateId, template);
        }
      } else {
        if (scope === 'SHARED') {
          await customReportService.createSharedTemplate(clientId, template);
        } else {
          await customReportService.createUserTemplate(clientId, uid, template);
        }
      }

      onSave();
    } catch (err: any) {
      console.error('Error saving template:', err);
      showError(err.message || t('customReports.messages.saveTemplateError', 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  // Cast TradesSourceSchema to SourceSchema for the embedded editor.
  // Memoised so the embedded editor's loadData effect does not re-fire on unrelated parent re-renders.
  // The backend now serves the correct schema per sourceType × assetClass (including
  // eventCode/NewTradeNumber for EVENT_LEVEL_REPORTS), so no client-side adaptation is needed.
  // NOTE: must be above the loading early-return so hooks are called in consistent order.
  const embeddedSchema: SourceSchema | undefined = useMemo(() => {
    if (!sourceSchema) return undefined;
    return {
      sourceType: resolvedSourceType,
      rowMode: sourceSchema.rowMode,
      coreFields: sourceSchema.coreFields,
      tradeFields: sourceSchema.tradeFields || [],
      groupingAvailable: sourceSchema.groupingAvailable,
      valuationFields: sourceSchema.valuationFields || [],
    };
  }, [sourceSchema, resolvedSourceType]);

  // ========== Render ==========

  if (loading) {
    return (
      <div className="custom-reports-page">
        <div className="cr-loading">
          <div className="spinner" />
          <p>{t('customReports.loadingEditor', 'Loading editor...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-reports-page">
      <div className="cr-toolbar">
        <h2>{isEditMode ? t('customReports.editor.editTitle', 'Edit Report') : t('customReports.editor.createTitle', 'New Report')}</h2>
      </div>

      {/* Settings — compact single row matching Output Formats editor */}
      <div className="editor-section settings-compact">
        <div className="settings-row">
          <div className="form-group settings-name">
            <label>{t('outputFormats.fields.name', 'Name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder={t('customReports.editor.namePlaceholder', 'Report name')}
            />
          </div>
          <div className="form-group" style={{ flex: 2, minWidth: '200px' }}>
            <label>{t('outputFormats.fields.description', 'Description')}</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={255}
              placeholder={t('customReports.editor.descriptionPlaceholder', 'Optional description')}
            />
          </div>
          <div className="form-group">
            <label>{t('customReports.editor.sourceTypeLabel', 'Level')}</label>
            {isEditMode ? (
              <span className="cr-format-badge" style={{ display: 'inline-block', padding: '0.35rem 0.6rem', fontSize: '0.8rem' }}>
                {resolvedSourceType === 'EVENT_LEVEL_REPORTS'
                  ? t('customReports.reportLevel.event', 'Event Level')
                  : t('customReports.reportLevel.trade', 'Trade Level')}
              </span>
            ) : (
              <div className="cr-level-toggle" style={{ marginBottom: 0 }}>
                <button
                  type="button"
                  className={`cr-level-btn${resolvedSourceType === 'TRADES' ? ' active' : ''}`}
                  onClick={() => setResolvedSourceType('TRADES')}
                >
                  {t('customReports.reportLevel.trade', 'Trade Level')}
                </button>
                <button
                  type="button"
                  className={`cr-level-btn${resolvedSourceType === 'EVENT_LEVEL_REPORTS' ? ' active' : ''}`}
                  onClick={() => setResolvedSourceType('EVENT_LEVEL_REPORTS')}
                >
                  {t('customReports.reportLevel.event', 'Event Level')}
                </button>
              </div>
            )}
          </div>
          <div className="form-group">
            <label>{t('customReports.editor.formatLabel', 'Format')}</label>
            <div className="format-selector">
              {(['CSV', 'XLSX', 'JSON'] as OutputFormat[]).map(fmt => (
                <button
                  key={fmt}
                  type="button"
                  className={`format-option ${outputFormat === fmt ? 'active' : ''}`}
                  onClick={() => setOutputFormat(fmt)}
                >
                  {fmt}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CSV Options — collapsible, only shown when format is CSV */}
      {outputFormat === 'CSV' && (
        <div className="editor-section collapsible-section">
          <div
            className="collapsible-header"
            onClick={() => setCsvOptionsExpanded(!csvOptionsExpanded)}
          >
            <span className={`expand-icon ${csvOptionsExpanded ? 'expanded' : ''}`}>▶</span>
            <h4>{t('outputFormats.sections.csvOptions', 'CSV Options')}</h4>
          </div>
          {csvOptionsExpanded && (
            <div className="collapsible-body">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('outputFormats.fields.delimiter', 'Delimiter')}</label>
                  <select
                    value={csvOptions.delimiter}
                    onChange={(e) => setCsvOptions({ ...csvOptions, delimiter: e.target.value })}
                  >
                    <option value=",">,</option>
                    <option value=";">;</option>
                    <option value="\t">Tab</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('outputFormats.fields.encoding', 'Encoding')}</label>
                  <select
                    value={csvOptions.encoding}
                    onChange={(e) => setCsvOptions({ ...csvOptions, encoding: e.target.value })}
                  >
                    <option value="UTF-8">UTF-8</option>
                    <option value="ISO-8859-1">ISO-8859-1</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('outputFormats.fields.lineEnding', 'Line Ending')}</label>
                  <select
                    value={csvOptions.lineEnding}
                    onChange={(e) => setCsvOptions({ ...csvOptions, lineEnding: e.target.value as 'CRLF' | 'LF' })}
                  >
                    <option value="CRLF">CRLF</option>
                    <option value="LF">LF</option>
                  </select>
                </div>
              </div>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={csvOptions.includeHeader}
                  onChange={(e) => setCsvOptions({ ...csvOptions, includeHeader: e.target.checked })}
                />
                {t('outputFormats.fields.includeHeader', 'Include header row')}
              </label>
            </div>
          )}
        </div>
      )}

      {/* Default Filters — collapsible, matches Output Formats CSV Options pattern */}
      <div className="editor-section collapsible-section">
        <div
          className="collapsible-header"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <span className={`expand-icon ${filtersExpanded ? 'expanded' : ''}`}>▶</span>
          <h4>{t('customReports.editor.defaultFilters', 'Default Filters')}</h4>
        </div>
        {filtersExpanded && (
          <div className="collapsible-body">
            <CustomReportFilterPanel
              filters={tradeFilters}
              onChange={setTradeFilters}
              filterOptions={filterOptions}
              sourceType={resolvedSourceType}
              assetClass={assetClass}
            />
          </div>
        )}
      </div>

      {/* Columns — embedded OutputFormatsTemplateEditor (provides its own section header) */}
      <div>
        <OutputFormatsTemplateEditor
          ref={editorRef}
          embedded
          embeddedSchema={embeddedSchema}
          clientId={clientId}
          sourceType={resolvedSourceType}
          assetClass={assetClass}
          onSave={() => {}}
          onCancel={() => {}}
          initialColumns={initialColumns}
          initialEnumMappings={initialEnumMappings}
          initialCalculatedColumns={initialCalculatedColumns}
          initialConditionalColumns={initialConditionalColumns}
          initialSortBy={initialSortBy}
        />
      </div>

      {/* Action bar */}
      <div className="cr-editor-actions">
        <button className="cr-btn" onClick={onCancel} type="button">
          {t('customReports.buttons.cancel', 'Cancel')}
        </button>
        <button
          className="cr-btn cr-btn-primary"
          onClick={handleSave}
          disabled={saving}
          type="button"
        >
          {saving ? t('customReports.buttons.saving', 'Saving...') : t('customReports.buttons.saveReport', 'Save Report')}
        </button>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default CustomReportTemplateEditor;
